import { ConvexError } from "convex/values";

/**
 * Centralized error codes for user-facing errors.
 * Using standard error code patterns (similar to GraphQL/HTTP conventions).
 */

export const errorCodes = {
  UNAUTHENTICATED: {
    code: "UNAUTHENTICATED",
    message: "Not authenticated",
    friendlyMessage: "Sua sessão expirou. Por favor, reinicie o app.",
  },
  USER_NOT_FOUND: {
    code: "USER_NOT_FOUND",
    message: "User not found",
    friendlyMessage: "Usuário não encontrado.",
  },
  INVALID_HANDLE: {
    code: "INVALID_HANDLE",
    message: "Invalid Instagram handle",
    friendlyMessage: "O @ inserido é inválido. Tente algo como 'instagram'.",
  },
  EMPTY_HANDLE: {
    code: "EMPTY_HANDLE",
    message: "Instagram handle cannot be empty",
    friendlyMessage: "Por favor, insira um @ para continuar.",
  },
  HANDLE_TOO_LONG: {
    code: "HANDLE_TOO_LONG",
    message: "Instagram handle exceeds maximum length of 30 characters",
    friendlyMessage: "O @ inserido é muito longo. O limite é de 30 caracteres.",
  },
  MONITOR_NOT_FOUND: {
    code: "MONITOR_NOT_FOUND",
    message: "Monitor not found",
    friendlyMessage: "Não encontramos este monitoramento.",
  },
  UNAUTHORIZED: {
    code: "UNAUTHORIZED",
    message: "Unauthorized",
    friendlyMessage: "Você não tem permissão para realizar esta ação.",
  },
  FAILED_CREATE_ACCOUNT: {
    code: "FAILED_CREATE_ACCOUNT",
    message: "Failed to create monitored account",
    friendlyMessage: "Não conseguimos iniciar o monitoramento. Tente novamente.",
  },
  API_KEY_NOT_CONFIGURED: {
    code: "API_KEY_NOT_CONFIGURED",
    message: "HIKER_API_KEY not configured",
    friendlyMessage: "Erro de configuração no servidor.",
  },
  PRIVATE_PROFILE: {
    code: "PRIVATE_PROFILE",
    message: "The profile is private",
    friendlyMessage: "Este perfil é privado. Só podemos monitorar perfis públicos.",
  },
  PRIVATE_ACCESS_DENIED: {
    code: "PRIVATE_ACCESS_DENIED",
    message: "The connected Instagram account cannot view this private profile",
    friendlyMessage:
      "A conta do Instagram conectada não tem acesso a este perfil privado.",
  },
  PRIVATE_SESSION_EXPIRED: {
    code: "PRIVATE_SESSION_EXPIRED",
    message: "The local Instagram session expired",
    friendlyMessage:
      "Sua sessão local do Instagram expirou. Conecte novamente para continuar.",
  },
  PRIVATE_DEVICE_UNAVAILABLE: {
    code: "PRIVATE_DEVICE_UNAVAILABLE",
    message: "The private device monitor is not available",
    friendlyMessage:
      "Não encontramos o monitor privado neste aparelho. Tente conectar novamente.",
  },
  PRIVATE_REFRESH_IN_PROGRESS: {
    code: "PRIVATE_REFRESH_IN_PROGRESS",
    message: "A private device refresh is already in progress",
    friendlyMessage:
      "Já existe uma atualização desse perfil em andamento neste aparelho.",
  },
  API_ERROR: (status: number) => ({
    code: "API_ERROR",
    message: `HikerAPI error: ${status}`,
    friendlyMessage: "Tivemos um problema ao conectar com o Instagram. Tente novamente.",
  }),
  API_TIMEOUT: {
    code: "API_TIMEOUT",
    message: "Request timed out",
    friendlyMessage: "A conexão expirou. Verifique sua internet e tente novamente.",
  },
  NETWORK_LOST: {
    code: "NETWORK_LOST",
    message: "Network connection lost during request",
    friendlyMessage: "A conexão foi perdida. Verifique sua internet e tente novamente.",
  },
  FREE_TIER_LIMIT_EXCEEDED: {
    code: "FREE_TIER_LIMIT_EXCEEDED",
    message: "Free tier monitor limit exceeded",
    friendlyMessage: "Você atingiu o limite gratuito de monitoramento. Atualize para o Premium para monitorar mais perfis.",
  },
  PREMIUM_TIER_LIMIT_EXCEEDED: {
    code: "PREMIUM_TIER_LIMIT_EXCEEDED",
    message: "Premium tier monitor limit exceeded",
    friendlyMessage: "Você atingiu o limite do Premium (5 monitores). Entre em contato para aumentar seu limite.",
  },
  GRACE_PERIOD_LIMIT_EXCEEDED: {
    code: "GRACE_PERIOD_LIMIT_EXCEEDED",
    message: "Grace period monitor limit exceeded",
    friendlyMessage: "Seu período de teste gratuito terminou. Atualize para o Premium para continuar monitorando até 5 perfis.",
  },
  PLAN_LIMIT_REACHED: {
    code: "PLAN_LIMIT_REACHED",
    message: "Plan monitor limit reached",
    friendlyMessage: "Você atingiu o limite do seu plano. Atualize para monitorar mais perfis.",
  },
  SUBSCRIPTION_REQUIRED: {
    code: "SUBSCRIPTION_REQUIRED",
    message: "Active subscription required",
    friendlyMessage:
      "Não conseguimos confirmar sua assinatura ativa. Toque em Restaurar compra ou tente novamente.",
  },
  TARGET_TOO_LARGE: {
    code: "TARGET_TOO_LARGE",
    message: "Target following count exceeds safety threshold",
    friendlyMessage: "Este perfil segue muitas contas e a análise pode ser instável. Tente outro perfil.",
  },
  TARGET_ALREADY_MONITORED: {
    code: "TARGET_ALREADY_MONITORED",
    message: "Target already monitored by user",
    friendlyMessage: "Você já monitora este perfil.",
  },
  TARGET_INFO_UNAVAILABLE: {
    code: "TARGET_INFO_UNAVAILABLE",
    message: "Unable to fetch target info",
    friendlyMessage: "Não conseguimos validar o perfil agora. Tente novamente.",
  },
  PRIVATE_SYNC_COOLDOWN: {
    code: "PRIVATE_SYNC_COOLDOWN",
    message: "Please wait before scanning this profile again",
    friendlyMessage:
      "Esse perfil foi escaneado recentemente. Aguarde antes de tentar novamente.",
  },
  PRIVATE_DAILY_LIMIT: {
    code: "PRIVATE_DAILY_LIMIT",
    message: "Daily device scan limit reached",
    friendlyMessage:
      "Você atingiu o limite diário de escaneamentos. Tente novamente amanhã.",
  },
} as const;

export type ErrorCode = keyof typeof errorCodes;

export interface PrivateProfileErrorData {
  username: string;
  fullName: string | null;
  profilePicUrl: string | null;
}

export type PrivateProfileConvexError = ConvexError<any> & {
  data: typeof errorCodes.PRIVATE_PROFILE & {
    profileData: PrivateProfileErrorData;
  };
};

export function createPrivateProfileError(
  profileData: PrivateProfileErrorData
): PrivateProfileConvexError {
  const error = new ConvexError({
    ...errorCodes.PRIVATE_PROFILE,
  }) as PrivateProfileConvexError;
  error.data.profileData = profileData;
  return error;
}
